/**
 * Renders YouTube videos found in notes.
 * Auto-detects YouTube URLs (watch?v=..., youtu.be/..., shorts/...) and renders
 * them as embedded iframes. Other text is rendered as-is.
 */

const YT_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})(?:[?&#][^\s]*)?/g;

export const extractYouTubeIds = (text) => {
  if (!text) return [];
  const ids = [];
  const matches = text.matchAll(YT_REGEX);
  for (const m of matches) {
    if (!ids.includes(m[1])) ids.push(m[1]);
  }
  return ids;
};

export const stripYouTubeUrls = (text) => {
  if (!text) return "";
  return text.replace(YT_REGEX, "").replace(/\n{3,}/g, "\n\n").trim();
};

export const YouTubeEmbed = ({ videoId, title = "YouTube video" }) => (
  <div
    className="relative w-full rounded-xl overflow-hidden bg-black"
    style={{ aspectRatio: "16/9" }}
    data-testid="youtube-embed"
  >
    <iframe
      src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
      title={title}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      className="absolute inset-0 w-full h-full"
      style={{ border: 0 }}
    />
  </div>
);

/**
 * Extracts YouTube IDs from notes and renders them above the notes text.
 * Returns { videoIds, cleanText } - cleanText has URLs stripped.
 */
export const YouTubeSection = ({ notes, lang = "ms" }) => {
  const videoIds = extractYouTubeIds(notes);
  if (videoIds.length === 0) return null;
  return (
    <div className="space-y-3 my-3" data-testid="youtube-section">
      {videoIds.length > 1 && (
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2563EB]">
          {lang === "en" ? `${videoIds.length} videos` : `${videoIds.length} video`}
        </p>
      )}
      {videoIds.map((id) => (
        <YouTubeEmbed key={id} videoId={id} />
      ))}
    </div>
  );
};
